// Sidebar credit-balance widget.
//
// Shows current total, plan label, a progress bar against the plan's
// monthly grant, and a rough "what you can still do" hint. CTA flips
// between "Upgrade" (free/starter) and "Manage" (pro/max) — both point
// to /billing; the page itself handles the portal redirect from there.
//
// Loading state is a static skeleton rather than a spinner to avoid
// layout jitter in the sidebar each render.

import { Link } from "react-router-dom";
import { Coins, ArrowUpRight, Settings } from "lucide-react";
import { useBalance } from "../lib/useBalance";
import { affordances, monthlyGrantFor, planLabel } from "../lib/credits";
import { cn } from "../lib/cn";

export default function BalanceWidget() {
  const { balance, isLoading, error } = useBalance();

  if (isLoading) {
    return (
      <div className="mx-3 mb-3 rounded-md border border-border bg-bg-raised/40 p-3">
        <div className="skeleton h-3 w-24 mb-2" />
        <div className="skeleton h-1.5 w-full mb-2" />
        <div className="skeleton h-3 w-32" />
      </div>
    );
  }

  if (error || !balance) {
    // Error state is intentionally quiet — a broken balance API
    // shouldn't block the rest of the app. We surface it faintly so
    // someone poking dev tools sees it, but users just see "—".
    return (
      <Link
        to="/billing"
        className="mx-3 mb-3 block rounded-md border border-border bg-bg-raised/40 p-3 hover:bg-bg-raised transition-colors"
        title={error?.message ?? "Balance unavailable"}
      >
        <div className="flex items-center gap-1.5 text-[11px] text-fg-subtle">
          <Coins size={12} strokeWidth={1.75} />
          Credits
        </div>
        <div className="text-sm text-fg-muted mt-1">— credits</div>
      </Link>
    );
  }

  const grant = monthlyGrantFor(balance.plan_id);
  // Progress = granted portion used, capped at 100%. Purchased credits
  // live "above" the bar — they don't expire, so showing them as % of
  // grant would be misleading.
  const grantedRemaining = balance.granted;
  const grantUsed = Math.max(0, grant - grantedRemaining);
  const pct = grant > 0 ? Math.min(100, Math.round((grantUsed / grant) * 100)) : 0;
  const hasPurchased = balance.purchased > 0;
  const aff = affordances(balance.total);
  const isUpgrade = balance.plan_id === "free" || balance.plan_id === "starter";

  return (
    <div className="mx-3 mb-3 rounded-md border border-border bg-bg-raised/40 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-fg-subtle">
          <Coins size={12} strokeWidth={1.75} />
          {planLabel(balance.plan_id)} plan
        </div>
      </div>

      <div className="flex items-baseline gap-1">
        <span className="font-mono tabular-nums text-lg text-fg">
          {balance.total.toLocaleString()}
        </span>
        <span className="text-[11px] text-fg-subtle">credits</span>
      </div>

      {/* Monthly grant — resets each billing cycle. Progress bar shows
          how much of the plan's monthly allocation is still unspent. */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-fg-subtle">
          <span>Monthly grant</span>
          <span className="font-mono tabular-nums text-fg-muted normal-case">
            {grantedRemaining}/{grant}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-bg-base overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              pct >= 90 ? "bg-warn" : "bg-accent",
            )}
            style={{ width: `${100 - pct}%` }}
            aria-label={`${grantedRemaining} of ${grant} monthly credits remaining`}
          />
        </div>
      </div>

      {/* Purchased top-ups — never expire, surfaced separately so users
          understand the two buckets don't live on the same clock. */}
      {hasPurchased && (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-fg-subtle">
          <span title="Purchased top-ups never expire">Top-ups</span>
          <span className="font-mono tabular-nums text-accent normal-case">
            {balance.purchased} <span className="text-fg-subtle">· never expires</span>
          </span>
        </div>
      )}

      <div className="text-[11px] text-fg-muted leading-tight pt-1 border-t border-border/60">
        <span className="font-mono tabular-nums">~{aff.builds}</span> builds
        <span className="text-fg-subtle"> · </span>
        <span className="font-mono tabular-nums">{aff.summaries}</span> summaries
      </div>

      <Link
        to="/billing"
        className={cn(
          "flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs transition-colors",
          isUpgrade
            ? "bg-accent/15 text-accent hover:bg-accent/25"
            : "bg-bg-base/60 text-fg-muted hover:text-fg hover:bg-bg-base",
        )}
      >
        {isUpgrade ? (
          <>
            <ArrowUpRight size={12} strokeWidth={2} /> Upgrade
          </>
        ) : (
          <>
            <Settings size={12} strokeWidth={2} /> Manage
          </>
        )}
      </Link>
    </div>
  );
}
